
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { format, set } from "date-fns";
import { cn } from "@/lib/utils";
import { ptBR } from "date-fns/locale";
import { useQuery } from "@tanstack/react-query";
import { getClients } from "@/services/ClientService";
import { createAppointment, updateAppointment, AppointmentFormData } from "@/services/AppointmentService";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";

interface AppointmentFormProps {
  appointment?: any;
  clientId?: string;
  onSuccess?: () => void;
}

const timeOptions = Array.from({ length: 24 * 4 }, (_, i) => {
  const hour = Math.floor(i / 4);
  const minute = (i % 4) * 15;
  return {
    value: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
    label: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
  };
});

const appointmentTypes = [
  { value: "Audiência", label: "Audiência" },
  { value: "Reunião", label: "Reunião" },
  { value: "Prazo", label: "Prazo" },
  { value: "Despacho", label: "Despacho" },
  { value: "Outro", label: "Outro" }
];

const appointmentSchema = z.object({
  title: z.string().min(3, { message: "Título deve ter pelo menos 3 caracteres" }),
  clientId: z.string().optional(),
  type: z.string({ required_error: "Tipo é obrigatório" }),
  date: z.date({ required_error: "Data é obrigatória" }),
  time: z.string().optional(),
  location: z.string().optional(),
  notes: z.string().optional(),
  allDay: z.boolean().default(false),
});

type AppointmentFormValues = z.infer<typeof appointmentSchema>;

export default function AppointmentForm({ appointment, clientId, onSuccess }: AppointmentFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();

  const { data: clientsResult } = useQuery({
    queryKey: ['clients'],
    queryFn: () => getClients(false),
  });

  const clients = clientsResult?.data || [];

  const defaultValues: Partial<AppointmentFormValues> = appointment ? {
    title: appointment.title || "",
    clientId: appointment.client_id || clientId || "",
    type: appointment.type || "",
    date: appointment.date ? new Date(appointment.date) : new Date(),
    time: appointment.date ? format(new Date(appointment.date), "HH:mm") : "",
    location: appointment.location || "",
    notes: appointment.notes || "",
    allDay: false,
  } : {
    title: "",
    clientId: clientId || "",
    type: "",
    date: new Date(),
    time: "",
    location: "",
    notes: "",
    allDay: false,
  };

  const form = useForm<AppointmentFormValues>({
    resolver: zodResolver(appointmentSchema),
    defaultValues,
  });

  const watchAllDay = form.watch("allDay");

  const onSubmit = async (data: AppointmentFormValues) => {
    console.log("Formulário submetido com dados:", data);
    setIsSubmitting(true);
    try {
      if (!user) {
        toast({
          title: "Erro de autenticação",
          description: "Você precisa estar logado para criar um compromisso.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      let appointmentDate = data.date;
      let endDate = undefined;
      
      if (!data.allDay && data.time) {
        const [hours, minutes] = data.time.split(":").map(Number);
        appointmentDate = set(data.date, { hours, minutes });
        // Set end date to 1 hour later by default
        endDate = set(data.date, { hours: hours + 1, minutes });
      }
      
      const appointmentData: AppointmentFormData = {
        title: data.title,
        type: data.type,
        date: appointmentDate,
        end_date: endDate,
        location: data.location || null,
        notes: data.notes || null,
        client_id: data.clientId || null,
      };

      console.log("Enviando dados para o Supabase:", appointmentData);

      let result;
      if (appointment?.id) {
        appointmentData.id = appointment.id;
        result = await updateAppointment(appointmentData);
      } else {
        result = await createAppointment(appointmentData);
      }
      
      console.log("Resultado da operação:", result);
      
      if (result.success) {
        toast({
          title: appointment ? "Compromisso atualizado" : "Compromisso agendado",
          description: appointment 
            ? "O compromisso foi atualizado com sucesso." 
            : "O compromisso foi agendado com sucesso.",
        });
        
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(result.error?.message || "Erro desconhecido");
      }
    } catch (error: any) {
      console.error("Erro ao salvar compromisso:", error);
      toast({
        title: "Erro ao salvar compromisso",
        description: error.message || "Ocorreu um erro ao tentar salvar o compromisso.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3 dark:bg-white">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="dark:text-gray-700">Título *</FormLabel>
              <FormControl>
                <Input className="dark:bg-white" {...field} placeholder="Ex: Audiência de instrução, Reunião com cliente" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="clientId"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="dark:text-gray-700">Cliente</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="dark:bg-white dark:text-gray-400">
                    <SelectValue placeholder="Selecione um cliente (opcional)" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="dark:bg-white hover:dark:bg-white dark:text-gray-400">
                  {clients && clients.map(client => (
                    <SelectItem className="dark:bg-white hover:dark:bg-white dark:text-gray-400" key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="dark:text-gray-700">Tipo *</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="dark:bg-white dark:text-gray-400">
                    <SelectValue placeholder="Selecione um tipo" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="dark:bg-white hover:dark:bg-white dark:text-gray-400">
                  {appointmentTypes.map(type => (
                    <SelectItem className="dark:bg-white hover:dark:bg-white dark:text-gray-400" key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex items-center space-x-4">
          <FormField
            control={form.control}
            name="allDay"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                <FormControl>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="allDay"
                        className=" border-lawyer-primary data-[state=checked]:bg-lawyer-primary"
                      />
                    <label htmlFor="allDay" className="text-sm font-medium dark:text-gray-700">
                      Dia inteiro
                    </label>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-6">
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className="dark:text-gray-700">Data *</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal dark:bg-white dark:text-gray-400",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP", { locale: ptBR })
                        ) : (
                          <span>Selecione uma data</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 dark:bg-white dark:text-gray-400" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      disabled={(date) =>
                        date < new Date(new Date().setHours(0, 0, 0, 0))
                      }
                      initialFocus
                      locale={ptBR}

                      className="pointer-events-auto lex-table"
                      classNames={{
                          day_selected:'!bg-[#072a45] text-white !hover:bg-red-500 hover:text-white',
                      }}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <div className="grid grid-cols-2 gap-4">
            {!watchAllDay && (
              <FormField
                control={form.control}
                name="time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-700">Horário</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="dark:bg-white dark:text-gray-400">
                          <SelectValue placeholder="Selecione um horário" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="dark:bg-white hover:dark:bg-white dark:text-gray-400">
                        {timeOptions.map(time => (
                          <SelectItem className="dark:bg-white hover:dark:bg-white dark:text-gray-400" key={time.value} value={time.value}>{time.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="dark:text-gray-700">Local</FormLabel>
                  <FormControl>
                    <Input className="dark:bg-white dark:text-gray-400" {...field} placeholder="Ex: Escritório, Fórum, Online" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="dark:text-gray-700">Observações</FormLabel>
              <FormControl>
                <Textarea 
                  {...field} 
                  placeholder="Detalhes sobre o compromisso" 
                  className="resize-none dark:bg-white dark:text-gray-400"
                  rows={4}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2">
          <Button type="button" variant="outline" onClick={onSuccess} className="dark:text-white">
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitting} className="flex items-center gap-2 bg-[#072a45] hover:bg-[#072a45] dark:bg-[#d4af37] dark:hover:bg-[#d4af37] text-white dark:text-[#072a45]">
            {isSubmitting ? "Salvando..." : appointment ? "Atualizar" : "Agendar"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
